"use client";

import { MessageCircle, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { whatsappDoCarrinho } from "@/lib/painel/carrinhoContato";

// "Salvar carrinho no WhatsApp" (2026-10-30): captura o WhatsApp ANTES do
// checkout -- página do carrinho, mini carrinho e janela ao sair da página do
// carrinho. Manda o número ao servidor (/api/checkout/cart-signal, etapa
// "carrinho"), que lê o carrinho real e repassa ao painel de atendimento: se
// o cliente não comprar, a régua do painel manda o link do carrinho pelo
// WhatsApp. Salvar é o aceite (o texto diz isso); "PARAR" continua valendo.

const STORAGE_KEY = "persi_carrinho_whatsapp";
const EXIT_SHOWN_KEY = "persi_carrinho_saida_mostrada";
export const CART_WHATSAPP_SAVED_EVENT = "persi:carrinho-whatsapp-salvo";

type Captura = "carrinho" | "mini_carrinho" | "saida";

function readSaved(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function maskInput(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function maskedSaved(whatsapp: string): string {
  return `(${whatsapp.slice(2, 4)}) •••••-${whatsapp.slice(-4)}`;
}

function useSavedWhatsapp() {
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    const sync = () => setSaved(readSaved());
    sync();
    window.addEventListener(CART_WHATSAPP_SAVED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CART_WHATSAPP_SAVED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return saved;
}

async function sendCapture(whatsapp: string, captura: Captura): Promise<boolean> {
  try {
    const response = await fetch("/api/checkout/cart-signal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ whatsapp, etapa: "carrinho", optin_whatsapp: true, captura }),
      credentials: "same-origin",
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  }
}

function CaptureForm({ captura, compact, onSaved, autoFocus }: { captura: Captura; compact?: boolean; onSaved?: () => void; autoFocus?: boolean }) {
  const inputId = useId();
  const hintId = useId();
  const [value, setValue] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "error">("idle");

  async function submit(event: FormEvent) {
    event.preventDefault();
    const whatsapp = whatsappDoCarrinho(value);
    if (!whatsapp) {
      setState("error");
      return;
    }
    setState("sending");
    const ok = await sendCapture(whatsapp, captura);
    if (!ok) {
      setState("error");
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, whatsapp);
    } catch {
      // Sem armazenamento: só não lembra que já salvou.
    }
    window.dispatchEvent(new Event(CART_WHATSAPP_SAVED_EVENT));
    setState("idle");
    onSaved?.();
  }

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <label htmlFor={inputId} className={compact ? "sr-only" : "text-sm font-medium text-slate-700"}>Seu WhatsApp</label>
      <div className={`flex gap-2 ${compact ? "" : "mt-1"}`}>
        <input
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="(11) 98765-4321"
          value={value}
          autoFocus={autoFocus}
          onChange={(event) => {
            setValue(maskInput(event.target.value));
            if (state === "error") setState("idle");
          }}
          aria-invalid={state === "error" ? true : undefined}
          aria-describedby={hintId}
          className={`min-w-0 flex-1 rounded-xl border border-slate-300 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${compact ? "h-9" : "h-11"}`}
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className={`shrink-0 rounded-xl bg-[#16a34a] px-4 text-sm font-semibold text-white transition hover:bg-[#15803d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16a34a] focus-visible:ring-offset-2 disabled:opacity-60 ${compact ? "h-9" : "h-11"}`}
        >
          {state === "sending" ? "Salvando…" : "Salvar"}
        </button>
      </div>
      <p id={hintId} className={`mt-1.5 text-[11px] leading-4 ${state === "error" ? "text-red-700" : "text-muted"}`} role={state === "error" ? "alert" : undefined}>
        {state === "error"
          ? "Confira o número com DDD (ex.: 11 98765-4321) e tente de novo."
          : "Ao salvar, você aceita receber o link do seu carrinho pelo WhatsApp. Responda PARAR para não receber mais."}
      </p>
    </form>
  );
}

function SavedNote({ whatsapp, compact }: { whatsapp: string; compact?: boolean }) {
  return (
    <p className={`flex items-center gap-2 text-emerald-800 ${compact ? "text-xs" : "text-sm"}`} role="status">
      <MessageCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
      Carrinho salvo no WhatsApp {maskedSaved(whatsapp)}.
    </p>
  );
}

/** Cartão na página do carrinho (abaixo do "Finalizar compra"). */
export function CartWhatsAppCaptureCard() {
  const saved = useSavedWhatsapp();
  return (
    <section className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" aria-labelledby="cart-whatsapp-title">
      {saved ? (
        <SavedNote whatsapp={saved} />
      ) : (
        <>
          <h3 id="cart-whatsapp-title" className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <MessageCircle className="h-4 w-4 text-[#16a34a]" aria-hidden="true" />
            Salve seu carrinho no WhatsApp
          </h3>
          <p className="mt-1 text-xs leading-5 text-slate-600">Precisa terminar depois? Mandamos o link do carrinho no seu WhatsApp.</p>
          <div className="mt-2">
            <CaptureForm captura="carrinho" />
          </div>
        </>
      )}
    </section>
  );
}

/** Versão compacta, no fim da lista do mini carrinho (o rodapé com o botão de compra fica livre). */
export function MiniCartWhatsAppCapture() {
  const saved = useSavedWhatsapp();
  const [open, setOpen] = useState(false);
  if (saved) {
    return (
      <div className="mx-0 mt-3 rounded-xl bg-emerald-50 px-3 py-2">
        <SavedNote whatsapp={saved} compact />
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2">
      {open ? (
        <>
          <p className="mb-1.5 text-xs font-semibold text-slate-800">Salvar carrinho no WhatsApp</p>
          <CaptureForm captura="mini_carrinho" compact autoFocus />
        </>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="flex min-h-9 w-full items-center gap-2 text-left text-xs font-semibold text-emerald-800">
          <MessageCircle className="h-4 w-4 shrink-0 text-[#16a34a]" aria-hidden="true" />
          Vai terminar depois? Salve o carrinho no seu WhatsApp
        </button>
      )}
    </div>
  );
}

/**
 * Janela ao sair da página do carrinho (computador: mouse saindo pelo topo),
 * uma vez por sessão, só com itens e sem WhatsApp salvo.
 */
export function CartExitWhatsAppCapture({ active }: { active: boolean }) {
  const saved = useSavedWhatsapp();
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!active || saved) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;
    let shown = false;
    try {
      shown = window.sessionStorage.getItem(EXIT_SHOWN_KEY) === "1";
    } catch {
      shown = false;
    }
    if (shown) return;
    const onLeave = (event: MouseEvent) => {
      if (event.relatedTarget || event.clientY > 0) return;
      try {
        window.sessionStorage.setItem(EXIT_SHOWN_KEY, "1");
      } catch {
        // segue sem lembrar
      }
      document.removeEventListener("mouseout", onLeave);
      setOpen(true);
    };
    // Espera um pouco: quem acabou de chegar ainda está olhando o carrinho.
    const timer = window.setTimeout(() => document.addEventListener("mouseout", onLeave), 4000);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mouseout", onLeave);
    };
  }, [active, saved]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      onClose={() => setOpen(false)}
      onClick={(event) => {
        if (event.target === event.currentTarget) setOpen(false);
      }}
      className="m-auto w-[min(92vw,420px)] rounded-2xl p-0 shadow-2xl backdrop:bg-slate-900/50"
    >
      <div className="relative p-6">
        <button type="button" onClick={() => setOpen(false)} aria-label="Fechar" className="absolute right-3 top-3 rounded-full p-2 text-slate-500 hover:bg-slate-100">
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
          <MessageCircle className="h-6 w-6 text-[#16a34a]" aria-hidden="true" />
        </span>
        <h2 id={titleId} className="mt-3 text-xl font-bold text-heading">Quer que a gente guarde seu carrinho?</h2>
        <p className="mt-1 text-sm leading-6 text-slate-600">Deixe seu WhatsApp e mandamos o link para você terminar a compra quando quiser.</p>
        <div className="mt-4">
          {saved ? <SavedNote whatsapp={saved} /> : <CaptureForm captura="saida" autoFocus onSaved={() => window.setTimeout(() => setOpen(false), 1200)} />}
        </div>
      </div>
    </dialog>
  );
}
